import { IDirectionDepricated, IMLResult } from "../../types/types";
import { IDeviceBasicCompDepricated } from "./deviceTypes";
import {
  createNormalVectorPath,
  generateRandomString,
  getCenterOfBoundingBox,
  getDeeplyNestedValue,
  getDeviceCategoryForMcBimItem,
  getDirectionFromMlResultItem,
  getMcBimInstanceFromMlResult,
  getPixPathForCeilingGrid,
  getRotatedBoundingBox,
  getSqaureAroundCoordinate,
  isReturnAirterminal,
  isSupplyAirTerminal,
  PartialMcBimSpace,
} from "../../utils";
import {
  DEVICE_DISCIPLINE_MAPPING,
  DeviceCategoryEnum,
  DEVICES_TYPES_SETTINGS,
  DisciplineEnum,
  RIGHT_CLICK_DEVICE_MENU_ENUM,
} from "../../enums";
import { IPixPath, IPixPolygon } from "../../components/PixBimViewer/types";
import {
  IBoundingBox,
  IDevice,
  IDeviceType,
  IDirection,
  IMcBimItem,
  IMLPredictDevice,
  IRoomSummary,
  IRoomsSummaryHashmap,
} from "@backend/src/schemas";

interface IAddDevicesArgs {
  mcbimSpace: PartialMcBimSpace;
  room?: IRoomSummary;
  mlResults: IMLResult[];
  mcBimItemsInStandardSpace: IMcBimItem[];
  supplyAirFlow: number | undefined;
  returnAirFlow: number | undefined;
  compCodePath: string;
  projectNo: number;
}

export const convertMlResultAndMcBimItemsInSpaceToDevice = (args: IAddDevicesArgs): IDevice[] => {
  return args.mlResults.reduce((acc, mlResult) => {
    if (
      mlResult.bounding_box?.every((point) => point.every((coord) => typeof coord !== "number"))
    ) {
      return acc;
    }
    const device: IDevice = {
      updatedAt: new Date().toISOString(),
      createdAt: new Date().toISOString(),
      projectNo: String(args.projectNo),
      roomNumber: args.mcbimSpace.name,
      floor: args.mcbimSpace.buildingStory?.name ?? "",
      revitCeilingHeight:
        args.room?.ceilingCoordinate != null ? args.room?.ceilingCoordinate[2] : undefined,
      /* pns: args.mcbimSpace.PNS ?? "-",*/
      ...getDeviceBasicCompFromMlResult({
        mlResult,
        spaceSupplyAirFlow: args.supplyAirFlow ?? 0,
        spaceReturnAirFlow: args.returnAirFlow ?? 0,
        mcBimItemsInStandardSpace: args.mcBimItemsInStandardSpace,
        compCodePath: args.compCodePath,
      }),
    };
    acc.push(device);
    return acc;
  }, [] as IDevice[]);
};

interface IConvertMcBimToDeviceArgs {
  mcBimItem: IMcBimItem;
  roomNumber: string;
  compCodePath: string;
  projectNo: number;
}

export const convertMcBimItemToDevice = (args: IConvertMcBimToDeviceArgs): IDevice | undefined => {
  const deviceCategory = getDeviceCategoryForMcBimItem({
    mcbimItem: args.mcBimItem,
    compCodePath: args.compCodePath,
  });
  if (deviceCategory == null) {
    return undefined;
  }
  return {
    compCode: getDeeplyNestedValue(args.mcBimItem, args.compCodePath) ?? "",
    description: args.mcBimItem.type?.name ?? "",
    boundingBox: args.mcBimItem.bbox as IBoundingBox,
    coordinate: getCenterOfBoundingBox(args.mcBimItem.bbox as IBoundingBox),
    direction: [0, 0],
    deviceCategory:
      getDeviceCategoryForMcBimItem({
        mcbimItem: args.mcBimItem,
        compCodePath: args.compCodePath,
      }) ?? DeviceCategoryEnum.DEFAULT_DEVICE,
    discipline: DEVICE_DISCIPLINE_MAPPING[deviceCategory],
    projectNo: String(args.projectNo),
    roomNumber: args.roomNumber,
    floor: args.mcBimItem.buildingStory?.name ?? "",
  };
};

interface IGetDeviceBasicCompFromMlResultArgs {
  mlResult: IMLResult;
  spaceSupplyAirFlow: number;
  spaceReturnAirFlow: number;
  mcBimItemsInStandardSpace: IMcBimItem[];
  compCodePath: string;
}

export const getDeviceBasicCompFromMlResult = (
  args: IGetDeviceBasicCompFromMlResultArgs
): IDeviceBasicCompDepricated => {
  const mcbimInstance = getMcBimInstanceFromMlResult(
    args.mlResult,
    args.mcBimItemsInStandardSpace,
    args.compCodePath as string
  );

  let device: IDeviceBasicCompDepricated = {
    compCode: (mcbimInstance?.psets?.NONS_Reference.RefCompType as string) ?? "-",
    description: (mcbimInstance?.type?.name as string) ?? "-",
    productName: (mcbimInstance?.psets?.NRH_PG_Info.Mengdetype as string) ?? "-",
    boundingBox: args.mlResult.bounding_box as IBoundingBox,
    coordinate:
      args.mlResult.bounding_box != null
        ? getCenterOfBoundingBox(args.mlResult.bounding_box)
        : [0, 0],
    direction: getDirectionFromMlResultItem(args.mlResult) as IDirection,
    deviceCategory: args.mlResult.device_type,
    grid: args.mlResult.grid,
    discipline: DEVICE_DISCIPLINE_MAPPING[args.mlResult.device_type] ?? DisciplineEnum.UNKNOWN,
  };

  if (mcbimInstance == null) {
    return device;
  } else if (isSupplyAirTerminal(mcbimInstance, args.compCodePath as string)) {
    const supplyAirFlow = args.spaceSupplyAirFlow;
    const nrOfSupplyAirTerminalsInSpace = args.mcBimItemsInStandardSpace.filter((mcbimItem) =>
      isSupplyAirTerminal(mcbimItem, args.compCodePath)
    ).length;
    device = {
      ...device,
      supplyAirFlow: supplyAirFlow / nrOfSupplyAirTerminalsInSpace,
    };
  } else if (isReturnAirterminal(mcbimInstance, args.compCodePath)) {
    const returnAirFlow = args.spaceReturnAirFlow;
    const nrOfReturnAirTerminalsInSpace = args.mcBimItemsInStandardSpace.filter((mcbimItem) =>
      isReturnAirterminal(mcbimItem, args.compCodePath)
    ).length;
    device = {
      ...device,
      returnAirFlow: returnAirFlow / nrOfReturnAirTerminalsInSpace,
    };
  }
  return device;
};

const rightClicMenuWithoutRotate = Object.entries(RIGHT_CLICK_DEVICE_MENU_ENUM).reduce(
  (acc, [key, value]) => {
    if (value === RIGHT_CLICK_DEVICE_MENU_ENUM.ROTATE) {
      return acc;
    }
    acc.push(value);
    return acc;
  },
  [] as string[]
);

const rightClickMenuWithRotate = Object.values(RIGHT_CLICK_DEVICE_MENU_ENUM);

export type IDeviceWithMovable = IDevice & { isMoveable: boolean };
interface IArgsConvertDevice {
  devices: IDevice[];
  typeDevices: IDeviceType[];
  selectedDeviceId: string | null;
  hideDevices: boolean;
  showTrueSize: boolean;
  isMoveable: boolean;
  rooms?: IRoomsSummaryHashmap;
}

/**
 * Standalone single-device converter.
 * Everything it needs is passed in, so it can be used outside convertDevicesToPixPolygons.
 */
interface IArgsConvertSingleDevice {
  deviceItem: IDevice;
  typeDevices: IDeviceType[];
  selectedDeviceId: string | null;
  showTrueSize: boolean;
  isMoveable: boolean;
  room?: IRoomsSummaryHashmap[string];
}

export const convertSingleDeviceToPixPolygon = (
  args: IArgsConvertSingleDevice
): IPixPolygon | null => {
  const { deviceItem, typeDevices, selectedDeviceId, showTrueSize, isMoveable, room } = args;

  if (deviceItem == null) {
    return null;
  }

  const isLocked = room?.multiplacerCeilingDevicesLocked?.bool === true;

  // Build default polygon (square around coordinate) unless missing required geometry inputs
  let resultPolygon: number[][] | null = null;
  if (deviceItem.coordinate != null && deviceItem.grid == null && deviceItem.direction != null) {
    resultPolygon = getSqaureAroundCoordinate(
      deviceItem.coordinate as [number, number],
      deviceItem.direction as [number, number]
    );
  }
  if (resultPolygon == null) {
    return null;
  }

  const color = DEVICES_TYPES_SETTINGS[deviceItem.deviceCategory]?.color || "#000000";

  const deviceType = typeDevices.find((t) => t.compCode === deviceItem.compCode);

  let trueSizePolygon: number[][] | undefined;
  if (deviceType != null && showTrueSize) {
    trueSizePolygon = getRotatedBoundingBox({
      boundingBox: deviceType.boundingBox,
      coordinate: deviceItem.coordinate as [number, number],
      direction: deviceItem.direction as [number, number],
    });
  }

  const pixPolygon: IPixPolygon = {
    id: deviceItem._id as string,
    points: trueSizePolygon ?? resultPolygon,
    fillColor: color,
    strokeColor: "cyan",
    strokeWidth: selectedDeviceId === deviceItem._id ? "10" : "0.05",
    text: {
      show: true,
      values: isLocked ? ["LÅST"] : [getDeviceText(deviceItem)],
      color: "black",
      fontSize: "1",
    },
    tooltip: undefined,
    isMoveable: isLocked ? false : isMoveable,
    isDragSelectable: false,
    rightClickMenu:
      deviceItem.deviceCategory === DeviceCategoryEnum.SUPPLY_AIR_DEVICE
        ? rightClickMenuWithRotate
        : rightClicMenuWithoutRotate,
  };

  return pixPolygon;
};

const isNotNull = <T>(v: T | null | undefined): v is T => v != null;

export const convertDevicesToPixPolygons = (args: IArgsConvertDevice): IPixPolygon[] => {
  if (args.devices == null || args.devices.length === 0 || args.hideDevices) {
    return [];
  }

  return args.devices
    .map((deviceItem) =>
      convertSingleDeviceToPixPolygon({
        deviceItem,
        typeDevices: args.typeDevices,
        selectedDeviceId: args.selectedDeviceId,
        showTrueSize: args.showTrueSize,
        isMoveable: args.isMoveable,
        room: args.rooms?.[deviceItem.roomNumber],
      })
    )
    .filter(isNotNull)
    .filter((pixPolygon) => viewerDeviceHasValidPolygons(pixPolygon));
};

export const convertGenereicMlResultToPixPolygon = (
  mlPredictedDevice: IMLPredictDevice
): IPixPolygon => {
  const color = DEVICES_TYPES_SETTINGS[mlPredictedDevice.deviceCategory]?.color || "#000000";
  const box = getSqaureAroundCoordinate(mlPredictedDevice.coordinate, mlPredictedDevice.direction);
  return {
    id: generateRandomString(10),
    points: box,
    fillColor: color,
    strokeColor: "cyan",
    strokeWidth: "0.05",
    text: {
      show: true,
      values: [],
      color: "black",
      fontSize: "1",
    },
    tooltip: undefined, //, getTooltip(),
    isMoveable: false,
    isDragSelectable: false,
  };
};

export const viewerDeviceHasValidPolygons = (vDevice: IPixPolygon): boolean => {
  if (!vDevice?.points) {
    return false;
  }
  return vDevice?.points?.every((polygon) => {
    return polygon.every((coord) => typeof coord === "number");
  });
};

export const getDeviceText = (deviceItem: IDevice): string => {
  if (typeof deviceItem.compCode === "string") {
    return deviceItem.compCode;
  }
  return "-";
};

export const convertDevicesToPixPaths = (devices: IDevice[]): IPixPath[] => {
  return devices.flatMap((device) => {
    if (device.grid != null) {
      return getPixPathForCeilingGrid(device.grid);
    }
    return [];
  });
};

export const getViewerPathAirTerminalDirectionsFromDevices = (
  devices: IDevice[],
  hideDevices: boolean
): IPixPath[] => {
  if (devices == null || hideDevices) {
    return [];
  }
  return devices.reduce((acc, device) => {
    const deviceSetting = DEVICES_TYPES_SETTINGS[device.deviceCategory];
    if (deviceSetting?.showDirectionInViewer !== true) return acc;
    const vectorPath = createNormalVectorPath(device.coordinate, device.direction);
    if (vectorPath == null) return acc;
    acc.push(vectorPath);
    return acc;
  }, [] as IPixPath[]);
};

export const copyDeviceAndMoveOneSquare = (device: IDevice) => {
  if (device.coordinate == null) {
    return;
  }
  const newDevice = { ...device, _id: undefined }; // Kopiera enheten och ta bort _id
  newDevice.coordinate = [device.coordinate[0] + 0.6, device.coordinate[1]];
  newDevice.boundingBox = getSqaureAroundCoordinate(
    newDevice.coordinate,
    newDevice.direction as [number, number]
  ) as IBoundingBox;
  return newDevice;
};

export interface IPointAPointB {
  pointA: {
    x: number;
    y: number;
  };
  pointB: {
    x: number;
    y: number;
  };
}

export const calculateTileBoundingBoxFromCoordinateAndDirection = (
  pointAPointB: IPointAPointB
): IBoundingBox => {
  // Create a boundingbox, 600x600mm, with the insertion point as the center. Rotate it to vector from insertionPoint with direction
  const halfSize = 0.3;
  const x = pointAPointB.pointA.x;
  const y = pointAPointB.pointA.y;
  const x1 = x + halfSize;
  const x2 = x - halfSize;
  const y1 = y + halfSize;
  const y2 = y - halfSize;
  const boundingBox: IBoundingBox = [
    [x1, y1],
    [x2, y1],
    [x2, y2],
    [x1, y2],
    [x1, y1],
  ];
  //Rotate the bounding box to the direction, created from a vector from insertionPoint to insertionPoint + direction
  const directionInsertionVector = [pointAPointB.pointB.x - x, pointAPointB.pointB.y - y];
  const angle = Math.atan2(directionInsertionVector[1], directionInsertionVector[0]);
  return boundingBox.map((point) => {
    const x = point[0] - pointAPointB.pointA.x;
    const y = point[1] - pointAPointB.pointA.y;
    const xRot = x * Math.cos(angle) - y * Math.sin(angle);
    const yRot = x * Math.sin(angle) + y * Math.cos(angle);
    return [xRot + pointAPointB.pointA.x, yRot + pointAPointB.pointA.y];
  }) as IBoundingBox;
};

export const getPointASubPointB = (pointAPointB: IPointAPointB): IDirectionDepricated => {
  return [
    pointAPointB.pointB.x - pointAPointB.pointA.x,
    pointAPointB.pointB.y - pointAPointB.pointA.y,
  ];
};

export const convertAVectorTo60cm = (vector: IDirectionDepricated): IDirectionDepricated => {
  const length = Math.sqrt(vector[0] ** 2 + vector[1] ** 2);
  const factor = 0.6 / length;
  return [vector[0] * factor, vector[1] * factor];
};

export const moveBoundingBox = (
  boundingBox: IBoundingBox,
  newInsertionPoint: { x: number; y: number }
): IBoundingBox => {
  const xDiff = newInsertionPoint.x - boundingBox[0][0];
  const yDiff = newInsertionPoint.y - boundingBox[0][1];
  return boundingBox.map((point) => [point[0] + xDiff, point[1] + yDiff]) as IBoundingBox;
};
